import Link from '@/components/Link';
import FrontPinkButton from '@/components/buttons/primary/FrontPinkButton';
import { Container } from '@/components/layout/Container';
import { PRIMARY_NAV_HEIGHT } from '@/components/navigation/PrimaryNavigation';
import {
  DOWNLOAD_PAGE_DESKTOP_APPS,
  DOWNLOAD_PAGE_HEADLINE,
  DOWNLOAD_PAGE_MOBILE_APPS,
  DOWNLOAD_PAGE_TEXT,
  DOWNLOAD_PAGE_WEB_APP_LINK_TEXT,
  DOWNLOAD_PAGE_WEB_APP_URL,
} from '@/constants/downloads';
import gtm from '@/lib/gtm';
import useBreakpoint from '@/lib/hooks/useBreakpoint';
import { getStaticPageProps } from '@/lib/next';
import { COLORS } from '@/styles/color';
import { marginX, mediaBreakpointUp, paddingX } from '@/styles/layout/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import MasterTemplate from '@/templates/MasterTemplate';
import { useMemo } from 'react';
import styled from 'styled-components';

const PageWrapper = styled.div`
  background: #ffffff;
  padding-bottom: ${remCalc(PRIMARY_NAV_HEIGHT)};
  padding-top: ${remCalc(PRIMARY_NAV_HEIGHT)};
`;

const Hero = styled.header`
  padding-top: ${remCalc(40)};
  padding-bottom: ${remCalc(36)};
  text-align: center;

  ${mediaBreakpointUp('desktop')} {
    padding-top: ${remCalc(80)};
  }

  h1 {
    ${customTextSize(40, 42)}
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    margin-bottom: ${remCalc(16)};

    max-width: ${remCalc(800)};
    ${marginX('auto')}

    ${mediaBreakpointUp('tablet')} {
      ${customTextSize(56, 53)}
    }

    ${mediaBreakpointUp('desktop')} {
      ${customTextSize(72, 70)}
    }
  }

  p {
    ${customTextSize(18, 29)}

    max-width: ${remCalc(900)};
    ${marginX('auto')}

    ${mediaBreakpointUp('tablet')} {
      ${customTextSize(22, 35)}
    }
  }
`;

const DownloadList = styled.ul`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${remCalc(16)};
  ${marginX('auto')}

  ${mediaBreakpointUp('tablet')} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${mediaBreakpointUp('desktop')} {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const DownloadItem = styled.li`
  width: 100%;
  background-color: ${COLORS.WHITE};
  border-radius: ${remCalc(10)};
  box-shadow: 0px 0px 30px rgba(0, 27, 56, 0.1);
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: ${COLORS.GRAY.HIGHLIGHT};
  }

  a {
    appearance: none;
    width: 100%;
    height: 100%;
    min-height: ${remCalc(220)};
    ${paddingX(24)}

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  h2 {
    ${customTextSize(24, 30)};
    font-weight: ${FONT_WEIGHTS.MEDIUM};
    margin-bottom: ${remCalc(8)};
    text-align: center;

    small {
      display: block;
      ${customTextSize(18, 30)};
    }
  }

  p {
    ${customTextSize(14, 16)};
  }
`;

const DownloadLogo = styled.div`
  width: ${remCalc(64)};
  height: ${remCalc(64)};
  border-radius: 100%;
  overflow: hidden;
  box-shadow: rgb(0 27 56 / 16%) 0px 4px 16px;
  margin-bottom: ${remCalc(20)};
`;

const WebAppButtonWrapper = styled.div`
  margin-top: ${remCalc(64)};
  text-align: center;
`;

const Index: PageTemplate = () => {
  const [currentBreakpoint, activeBreakpoints] = useBreakpoint();

  const apps = useMemo(() => {
    if (!activeBreakpoints.includes('desktop')) {
      return [...DOWNLOAD_PAGE_MOBILE_APPS, ...DOWNLOAD_PAGE_DESKTOP_APPS];
    }

    return [...DOWNLOAD_PAGE_DESKTOP_APPS, ...DOWNLOAD_PAGE_MOBILE_APPS];
  }, [activeBreakpoints]);

  const onAppClick = (label: string) => {
    gtm.track('click', {
      element_type: 'button',
      element_label: label,
      location: 'Download Page',
    });
  };

  return (
    <PageWrapper>
      <Hero>
        <Container>
          <h1>{DOWNLOAD_PAGE_HEADLINE}</h1>
          <p>{DOWNLOAD_PAGE_TEXT}</p>
        </Container>
      </Hero>

      <Container>
        <DownloadList>
          {apps.map((app) => (
            <DownloadItem key={app.key}>
              <Link href={app.url}>
                <a
                  title={`Download ${app.title}`}
                  target="_blank"
                  onClick={() => onAppClick(`${app.title} - ${app.type}`)}
                >
                  <DownloadLogo>{app.logo}</DownloadLogo>
                  {typeof app.title === 'string' ? (
                    <h2>{app.title}</h2>
                  ) : (
                    <h2>
                      {app.title[0]}
                      <small>{app.title[1]}</small>
                    </h2>
                  )}
                  <p>{app.type}</p>
                </a>
              </Link>
            </DownloadItem>
          ))}
        </DownloadList>

        <WebAppButtonWrapper>
          <FrontPinkButton
            as="a"
            href={DOWNLOAD_PAGE_WEB_APP_URL}
            onClick={() => {
              gtm.track('click', {
                element_type: 'link',
                element_label: 'Web App Button',
                location: 'Download Page',
              });
            }}
          >
            {DOWNLOAD_PAGE_WEB_APP_LINK_TEXT}
          </FrontPinkButton>
        </WebAppButtonWrapper>
      </Container>
    </PageWrapper>
  );
};

Index.template = MasterTemplate;

export default Index;

export const getStaticProps = getStaticPageProps('download');
